import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import { dialog } from "../Common/TreasuryDialog";
import { refreshToken, logout } from "../../redux/actions/authAction";
import { postDataAPI } from "../../utils/API";
import AdminShell from "../Admin/AdminShell";
import FormattedNumber from "../Common/FormattedNumber";

// Mobile breakpoint hook
const useIsMobile = (bp = 768) => {
  const [m, setM] = React.useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
};

const inputStyle = {
  width: "100%", padding: "11px 14px", border: "1.5px solid #e5e7eb", borderRadius: 9,
  fontSize: 14, color: "#111827", background: "#f9fafb", outline: "none",
  boxSizing: "border-box", fontFamily: "inherit",
};
const labelStyle = { fontSize: 12.5, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 };
const cardStyle = { background: "#fff", borderRadius: 16, padding: "28px 32px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 24 };

const Profile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobile = useIsMobile();
  const [authuser, setAuthUser] = useState({});
  const [errors, setErrors] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bankData, setBankData] = useState({ bankName: "", iban: "SA" });
  const [bankErrors, setBankErrors] = useState({});
  const [savingBank, setSavingBank] = useState(false);
  const [bankEditMode, setBankEditMode] = useState(false);
  const { auth } = useSelector((state) => state);

  const isAdmin = auth.data?.user?.role === 1;

  useEffect(() => {
    if (auth.data?.status === 1) {
      const u = auth.data.user;
      setAuthUser({ ...u, phone_no: u.phone_no ? u.phone_no.toString().slice(2) : "" });
      const hasSaved = !!(u.bankName && u.iban && u.iban.length === 24);
      setBankData({
        bankName: u.bankName || "",
        iban: u.iban || "SA",
      });
      setBankEditMode(!hasSaved);
    }
  }, [auth]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setAuthUser(prev => ({ ...prev, [name]: value }));
  };

  const handleBankInput = (e) => {
    const { name, value } = e.target;
    if (name === "iban") {
      // Always keep SA prefix, only allow digits after it
      const raw = value.toUpperCase();
      if (!raw.startsWith("SA")) return;
      const digits = raw.slice(2).replace(/\D/g, "").slice(0, 22);
      setBankData(prev => ({ ...prev, iban: "SA" + digits }));
    } else {
      setBankData(prev => ({ ...prev, [name]: value }));
    }
    setBankErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!bankData.bankName) errs.bankName = "Please select a bank";
    if (!bankData.iban || bankData.iban.length < 24) errs.iban = "IBAN must be SA followed by 22 digits (24 characters total)";
    if (Object.keys(errs).length) { setBankErrors(errs); return; }
    setSavingBank(true);
    try {
      const token = auth.data?.accesstoken || auth.data?.access_token;
      const phone = auth.data?.user.phone_no ? auth.data.user.phone_no.toString() : "";
      // phone stored as 05XXXXXXXX (10 chars), backend expects 8 digits after '05'
      const phoneDigits = phone.startsWith("05") ? phone.slice(2) : phone.startsWith("5") ? phone.slice(1) : phone;
      const payload = {
        user_id: auth.data?.user._id,
        name: auth.data?.user.name,
        email: auth.data?.user.email,
        national_id: auth.data?.user.national_id,
        phone_no: phoneDigits,
        new_password: "",
        repeat_password: "",
        bankName: bankData.bankName,
        iban: bankData.iban,
      };
      console.log("[BankSave] payload:", payload);
      const res = await postDataAPI("update_profile", payload, token);
      console.log("[BankSave] response:", res.data);
      if (res.data.status === 1) {
        dialog.success(t("dialog.savedTitle"), t("dialog.bankSaved"));
        setBankEditMode(false);
        dispatch(refreshToken());
      } else {
        const errMsg = res.data.errors ? JSON.stringify(res.data.errors) : "Failed to save bank details";
        dialog.error(t("dialog.errorTitle"), errMsg);
      }
    } catch (err) { console.error("[BankSave] error:", err); dialog.error(t("dialog.errorTitle"), t("dialog.bankSaveFailed")); }
    setSavingBank(false);
  };

  const SAUDI_BANKS = [
    "Al Rajhi Bank",
    "Saudi National Bank (SNB)",
    "Riyad Bank",
    "Saudi British Bank (SABB)",
    "Banque Saudi Fransi",
    "Arab National Bank (ANB)",
    "Alinma Bank",
    "Bank AlBilad",
    "Bank Aljazira",
    "Saudi Investment Bank (SAIB)",
    "Gulf International Bank (GIB)",
    "Citibank Saudi Arabia",
    "Deutsche Bank Saudi Arabia",
    "BNP Paribas Saudi Arabia",
    "JP Morgan Saudi Arabia",
    "HSBC Saudi Arabia",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { name, email, national_id, phone_no, new_password, repeat_password } = e.target.elements;
    try {
      const profileToken = auth.data?.accesstoken || auth.data?.access_token;
      const res = await postDataAPI("update_profile", {
        user_id: auth.data?.user._id,
        name: name.value, email: email.value, national_id: national_id.value,
        phone_no: phone_no.value, new_password: new_password.value, repeat_password: repeat_password.value,
      }, profileToken);
      if (res.data.status === 0) setErrors(res.data.errors || {});
      else { dialog.success(t("dialog.savedTitle"), t("dialog.profileUpdated")); setErrors({}); }
    } catch { dialog.error(t("dialog.errorTitle"), t("dialog.profileUpdateFailed")); }
    setSaving(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { dialog.error(t("dialog.fileTooLargeTitle"), t("dialog.fileTooLargeMsg")); return; }
    setImage(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!image) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", image);
    formData.append("user_id", auth.data?.user._id);
    const token = auth.data?.accesstoken || auth.data?.access_token;
    try {
      const res = await axios.post("/api/update_profilePic", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (res.data.status === 1) {
        dispatch(refreshToken());
        setShowUpload(false);
        setImage(null);
        dialog.success(t("dialog.updatedTitle"), t("dialog.profileImageUpdated"));
      } else {
        dialog.error(t("dialog.errorTitle"), res.data.msg || t("dialog.uploadFailed"));
      }
    } catch (err) {
      dialog.error(t("dialog.errorTitle"), err?.response?.data?.msg || t("dialog.uploadFailed"));
    }
    setUploading(false);
  };

  const user = auth.data?.user || {};
  const avatarSrc = user.profile_image
    ? (user.profile_image.startsWith("http") ? user.profile_image : null)
    : null;
  const initials = (user.name || "U").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  const userNavLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/howitwork", label: t("nav.howItWorks") },
    { to: "/blog", label: t("nav.news") },
    { to: "/contactus", label: t("nav.contact") },
    { to: "/dashboard/portfolio", label: t("nav.portfolio") },
  ];

  const adminNavLinks = [
    { to: "/admin/dashboard", label: t("admin.dashboard") },
    { to: "/admin/users", label: t("admin.users") },
    { to: "/admin/properties", label: t("admin.properties") },
    { to: "/admin/requestFund", label: t("admin.fundRequests") },
    { to: "/admin/integrations", label: t("admin.integrations") },
  ];

  const quickLinks = isAdmin ? adminNavLinks : userNavLinks;

  const profileContent = (
    <div style={{ minHeight: "100vh", background: "#f8f9fa" }}>
      {/* Page Content */}
      <div style={{ padding: "32px 24px", maxWidth: 960, margin: "0 auto" }}>
        {/* Page Title */}
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>{t("profile.title")}</h2>
          <p style={{ color: "#6b7280", fontSize: 14, margin: "4px 0 0" }}>{t("profile.subtitle")}</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "280px 1fr", gap: 24, alignItems: "start" }}>
          {/* Left — Avatar Card */}
          <div>
            <div style={{ ...cardStyle, textAlign: "center" }}>
              <div style={{ position: "relative", display: "inline-block", marginBottom: 16 }}>
                {avatarSrc ? (
                  <img src={avatarSrc} alt={user.name} style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: "3px solid #f0e9d6" }} />
                ) : (
                  <div style={{ width: 96, height: 96, borderRadius: "50%", background: "linear-gradient(135deg, #0e3725, #1a5c3a)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 700, color: "#c9a84c", margin: "0 auto" }}>
                    {initials}
                  </div>
                )}
                <button onClick={() => setShowUpload(true)} style={{ position: "absolute", bottom: 0, right: 0, width: 30, height: 30, borderRadius: "50%", background: "#c9a84c", border: "2px solid #fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="13" height="13" fill="none" stroke="#fff" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                </button>
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 4px" }}>{user.name || "—"}</h3>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 16px" }}>{user.email || "—"}</p>
              <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px", fontSize: 12.5, color: "#374151", textAlign: "left" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: "#9ca3af" }}>{t("auth.nationalId")}</span>
                  <span style={{ fontWeight: 600 }}>{user.national_id || "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: "#9ca3af" }}>{t("auth.phoneNumber")}</span>
                  <span style={{ fontWeight: 600 }}>+966 5{user.phone_no || "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>{t("profile.wallet")}</span>
                  <span style={{ fontWeight: 600, fontSize: 11, color: user.walletAddress ? "#059669" : "#9ca3af" }}>
                    {user.walletAddress ? `${user.walletAddress.slice(0, 8)}\u2026` : t("profile.notSet")}
                  </span>
                </div>
              </div>
              {!isAdmin && (
                <Link to="/dashboard/portfolio" style={{ display: "block", marginTop: 14, padding: "10px", background: "linear-gradient(135deg, #0e3725, #1a5c3a)", color: "#c9a84c", borderRadius: 9, fontSize: 13, fontWeight: 600, textDecoration: "none", textAlign: "center" }}>
                  {t("profile.viewTransactions")}
                </Link>
              )}
            </div>

            {/* Balance Card */}
            <div style={{ ...cardStyle, background: "linear-gradient(135deg, #0e3725, #1a5c3a)" }}>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", margin: "0 0 6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("portfolio.availableBalance")}</p>
              <p style={{ fontSize: 26, fontWeight: 800, color: "#c9a84c", margin: "0 0 4px" }}>
                <FormattedNumber value={user.totalBalance || 0} prefix="﷼ " decimals={2} decimalSize="0.65em" />
              </p>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", margin: 0 }}>{t("profile.saudiRiyal")}</p>
            </div>
           </div>
          {/* Right — Edit Form + Bank Account */}
          <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", margin: "0 0 20px", paddingBottom: 16, borderBottom: "1px solid #f3f4f6" }}>
              {t("profile.personalInfo")}
            </h3>
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>{t("auth.fullName")}</label>
                  <input name="name" type="text" value={authuser.name || ""} onChange={handleInput} style={inputStyle} placeholder={t("auth.fullName")} />
                  {errors.name && <span style={{ fontSize: 12, color: "#ef4444" }}>{errors.name}</span>}
                </div>
                <div>
                  <label style={labelStyle}>{t("auth.email")}</label>
                  <input name="email" type="email" value={authuser.email || ""} onChange={handleInput} style={inputStyle} placeholder={t("auth.email")} />
                  {errors.email && <span style={{ fontSize: 12, color: "#ef4444" }}>{errors.email}</span>}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>{t("auth.nationalId")}</label>
                  <input name="national_id" type="text" value={authuser.national_id || ""} onChange={handleInput} style={inputStyle} placeholder={t("auth.nationalId")} />
                  {errors.national_id && <span style={{ fontSize: 12, color: "#ef4444" }}>{errors.national_id}</span>}
                </div>
                <div>
                  <label style={labelStyle}>{t("auth.phoneNumber")}</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <div style={{ ...inputStyle, width: 72, flexShrink: 0, background: "#f3f4f6", color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>+966</div>
                    <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
                      <span style={{ position: "absolute", left: 12, color: "#374151", fontWeight: 600, fontSize: 14, pointerEvents: "none", userSelect: "none" }}>5</span>
                      <input name="phone_no" type="text" inputMode="numeric" maxLength={8} value={authuser.phone_no || ""} onChange={handleInput} style={{ ...inputStyle, paddingLeft: 22, flex: 1 }} placeholder="XXXXXXXX" />
                    </div>
                  </div>
                  {errors.phone_no && <span style={{ fontSize: 12, color: "#ef4444" }}>{errors.phone_no}</span>}
                </div>
              </div>

                <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 20, marginTop: 4 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: "0 0 16px" }}>{t("profile.changePassword")}</h4>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={labelStyle}>{t("profile.newPassword")}</label>
                    <input name="new_password" type="password" style={inputStyle} placeholder={t("profile.passwordPlaceholder")} />
                    {errors.new_password && <span style={{ fontSize: 12, color: "#ef4444" }}>{errors.new_password}</span>}
                  </div>
                  <div>
                    <label style={labelStyle}>{t("auth.confirmPassword")}</label>
                    <input name="repeat_password" type="password" style={inputStyle} placeholder={t("profile.repeatPasswordPlaceholder")} />
                    {errors.repeat_password && <span style={{ fontSize: 12, color: "#ef4444" }}>{errors.repeat_password}</span>}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button type="submit" disabled={saving} style={{ padding: "12px 28px", background: "linear-gradient(135deg, #c9a84c, #a07a20)", color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? t("common.saving") : t("profile.saveChanges")}
                </button>
              </div>
             </form>
          </div>

          {/* Bank Account Details Card */}
          <div style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg, #0e3725, #1a5c3a)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="18" height="18" fill="none" stroke="#c9a84c" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M6 15h2"/><path d="M10 15h4"/></svg>
              </div>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", margin: 0 }}>{t("profile.bankAccountDetails")}</h3>
                <p style={{ fontSize: 12.5, color: "#6b7280", margin: "2px 0 0" }}>{t("profile.bankAccountNote")}</p>
              </div>
            </div>
            {/* Display view — shown when bank details are saved and not in edit mode */}
            {!bankEditMode && bankData.bankName && bankData.iban && bankData.iban.length === 24 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {/* Saved details rows */}
                <div style={{ display: "flex", flexDirection: "column", gap: 0, border: "1px solid #f3f4f6", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                  {[
                    { label: t("profile.accountHolder"), value: user.name || "\u2014" },
                    { label: t("profile.bank"), value: bankData.bankName },
                    { label: t("profile.iban"), value: bankData.iban, mono: true },
                  ].map((row, i, arr) => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", background: i % 2 === 0 ? "#f9fafb" : "#fff", borderBottom: i < arr.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                      <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>{row.label}</span>
                      <span style={{ fontSize: 13.5, color: "#111827", fontWeight: 600, fontFamily: row.mono ? "monospace" : "inherit", letterSpacing: row.mono ? 1.2 : 0 }}>{row.value}</span>
                    </div>
                  ))}
                </div>
                {/* Verified badge */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
                  <svg width="14" height="14" fill="none" stroke="#059669" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span style={{ fontSize: 12, color: "#059669", fontWeight: 600 }}>{t("profile.bankVerified")}</span>
                </div>
                {/* Action buttons */}
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => setBankEditMode(true)}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}
                  >
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    {t("common.edit")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(t("profile.removeBankConfirm"))) {
                        setBankData({ bankName: "", iban: "SA" });
                        setBankEditMode(true);
                      }
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", background: "#fff", border: "1.5px solid #fecaca", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#dc2626", cursor: "pointer" }}
                  >
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                    {t("common.remove")}
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleBankSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Account Holder Name — pre-filled, read-only */}
              <div>
                <label style={labelStyle}>{t("profile.accountHolder")}</label>
                <input
                  type="text"
                  value={user.name || ""}
                  readOnly
                  style={{ ...inputStyle, background: "#f3f4f6", color: "#6b7280", cursor: "not-allowed" }}
                />
                <span style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 4, display: "block" }}>{t("profile.autoFilled")}</span>
              </div>
              {/* Bank Name */}
              <div>
                <label style={labelStyle}>{t("profile.bankName")}</label>
                <select
                  name="bankName"
                  value={bankData.bankName}
                  onChange={handleBankInput}
                  style={{ ...inputStyle, cursor: "pointer", appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center" }}
                >
                  <option value="">{t("profile.selectBank")}</option>
                  {SAUDI_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                {bankErrors.bankName && <span style={{ fontSize: 12, color: "#ef4444", marginTop: 4, display: "block" }}>{bankErrors.bankName}</span>}
              </div>
              {/* IBAN */}
              <div>
                <label style={labelStyle}>{t("profile.ibanNumber")}</label>
                <div style={{ position: "relative" }}>
                  <input
                    name="iban"
                    type="text"
                    value={bankData.iban}
                    onChange={handleBankInput}
                    maxLength={24}
                    style={{ ...inputStyle, letterSpacing: 1.5, fontFamily: "monospace", fontSize: 15, paddingRight: 40 }}
                    placeholder="SA + 22 digits"
                  />
                  <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: bankData.iban.length === 24 ? "#059669" : "#9ca3af", fontWeight: 600 }}>
                    {bankData.iban.length}/24
                  </span>
                </div>
                {bankData.iban.length > 2 && bankData.iban.length < 24 && (
                  <span style={{ fontSize: 12, color: "#f59e0b", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                    {t("profile.ibanLength", { remaining: 24 - bankData.iban.length })}
                  </span>
                )}
                {bankData.iban.length === 24 && (
                  <span style={{ fontSize: 12, color: "#059669", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                    {t("profile.ibanValid")}
                  </span>
                )}
                {bankErrors.iban && <span style={{ fontSize: 12, color: "#ef4444", marginTop: 4, display: "block" }}>{bankErrors.iban}</span>}
              </div>
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <svg width="16" height="16" fill="none" stroke="#d97706" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                <p style={{ fontSize: 12, color: "#92400e", margin: 0, lineHeight: 1.5 }}>{t("profile.kycNote")}</p>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                {bankEditMode && bankData.bankName && bankData.iban && bankData.iban.length === 24 && (
                  <button type="button" onClick={() => setBankEditMode(false)} style={{ padding: "12px 18px", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 9, fontSize: 14, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
                )}
                <button type="submit" disabled={savingBank} style={{ padding: "12px 28px", background: "linear-gradient(135deg, #c9a84c, #a07a20)", color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: savingBank ? "not-allowed" : "pointer", opacity: savingBank ? 0.7 : 1, boxShadow: "0 4px 14px rgba(201,168,76,0.35)" }}>
                  {savingBank ? t("common.saving") : t("profile.saveBankDetails")}
                </button>
              </div>
            </form>
            )}
          </div>
          </div>
        </div>
        {/* Quick Links — full width, bottom of page */}
        <div style={{ ...cardStyle, marginTop: 0 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: "#374151", margin: "0 0 12px", textTransform: "uppercase", letterSpacing: 0.5 }}>{t("profile.quickLinks")}</h4>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "0 32px" }}>
            {quickLinks.map(link => (
              <Link key={link.to} to={link.to} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #f3f4f6", color: "#374151", textDecoration: "none", fontSize: 13, fontWeight: 500 }}
                onMouseEnter={e => e.currentTarget.style.color = "#0e3725"}
                onMouseLeave={e => e.currentTarget.style.color = "#374151"}
              >
                {link.label}
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
              </Link>
            ))}
          </div>
        </div>
      </div>
      {/* Upload Modal */}
      {showUpload && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", width: "100%", maxWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0 }}>{t("profile.updatePicture")}</h3>
              <button onClick={() => { setShowUpload(false); setImage(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 20 }}>×</button>
            </div>
            <form onSubmit={handleUpload}>
              <div style={{ border: "2px dashed #e5e7eb", borderRadius: 10, padding: "24px", textAlign: "center", marginBottom: 16, background: "#f9fafb" }}>
                <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} id="profile-pic-input" />
                <label htmlFor="profile-pic-input" style={{ cursor: "pointer" }}>
                  {image ? (
                    <div>
                      <img src={URL.createObjectURL(image)} alt="Preview" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", margin: "0 auto 8px", display: "block" }} />
                      <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>{image.name}</p>
                    </div>
                  ) : (
                    <div>
                      <svg width="32" height="32" fill="none" stroke="#9ca3af" strokeWidth="1.5" viewBox="0 0 24 24" style={{ margin: "0 auto 8px", display: "block" }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>{t("profile.clickToSelectImage")}</p>
                    </div>
                  )}
                </label>
              </div>
              <button type="submit" disabled={!image || uploading} style={{ width: "100%", padding: "12px", background: image ? "linear-gradient(135deg, #c9a84c, #a07a20)" : "#e5e7eb", color: image ? "#fff" : "#9ca3af", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: image ? "pointer" : "not-allowed" }}>                {uploading ? t("common.uploading") : t("profile.uploadPicture")}           </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // Admin view: wrap with AdminShell sidebar
  if (isAdmin) {
    return (
      <AdminShell pageTitle="My Profile">
        {profileContent}
      </AdminShell>
    );
  }

  // User view: no extra header — DashboardLayout already provides DashboardHeader
  return profileContent;
};

export default Profile;
